import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useBack } from '../hooks';
import * as XLSX from 'xlsx';
import api from '../api';

const TEAL   = '#14b8a6';
const BG     = '#0f1117';
const CARD   = '#1e2329';
const BORDER = '#2a3040';
const MUTED  = '#6b7280';
const TEXT   = '#f0f2f5';
const RED    = '#ef4444';

const inp = {
  background: '#161b22', border: `1px solid ${BORDER}`, borderRadius: '8px',
  padding: '7px 11px', fontSize: '13px', color: TEXT, outline: 'none',
  width: '100%', boxSizing: 'border-box',
};

const PARTS_DATA_COLS  = ['target_cycle_s','shot_weight_g','part_weight_g','sprue_weight_g','scrap_pct','qty_per_box','qty_per_pallet'];
const MOULDS_DATA_COLS = ['cavities','sw_g','pw_g','toolchange_mount_min','toolchange_unmount_min'];
const COL_LABELS = {
  target_cycle_s:         'Cycle (s)',
  shot_weight_g:          'Shot (g)',
  part_weight_g:          'Part (g)',
  sprue_weight_g:         'Sprue (g)',
  scrap_pct:              'Scrap %',
  qty_per_box:            'Per Box',
  qty_per_pallet:         'Per Pallet',
  cavities:               'Cavities',
  sw_g:                   'Shot (g)',
  pw_g:                   'Part (g)',
  toolchange_mount_min:   'Mount (min)',
  toolchange_unmount_min: 'Unmount (min)',
};

function ActionBadge({ action, onClick }) {
  const styles = {
    update: { background: '#0f766e', color: '#5eead4', borderColor: '#0d9488' },
    create: { background: '#1e3a5f', color: '#93c5fd', borderColor: '#1d4ed8' },
  };
  const st = styles[action] || {};
  return (
    <button onClick={onClick} style={{
      fontSize: '10px', fontWeight: '700', borderRadius: '4px', padding: '2px 8px',
      border: `1px solid ${st.borderColor || BORDER}`,
      background: st.background || 'none', color: st.color || MUTED,
      cursor: onClick ? 'pointer' : 'default',
    }}>
      {action.toUpperCase()}
    </button>
  );
}

// ── Smart tab (CSV / XLSX + AI analysis) ──────────────────────────────────────

const PARTS_FIELD_OPTIONS = [
  { value: 'skip',             label: '— Skip —' },
  { value: 'name',             label: 'Part name' },
  { value: 'part_number',      label: 'Part number' },
  { value: 'target_cycle_s',   label: 'Cycle time (s)' },
  { value: 'shot_weight_g',    label: 'Shot weight (g)' },
  { value: 'part_weight_g',    label: 'Part weight (g)' },
  { value: 'sprue_weight_g',   label: 'Sprue weight (g)' },
  { value: 'scrap_pct',        label: 'Scrap %' },
  { value: 'qty_per_box',      label: 'Qty per box' },
  { value: 'qty_per_pallet',   label: 'Qty per pallet' },
];
const MOULDS_FIELD_OPTIONS = [
  { value: 'skip',                   label: '— Skip —' },
  { value: 'ref',                    label: 'Mould ref' },
  { value: 'name',                   label: 'Mould name' },
  { value: 'cavities',               label: 'Cavities' },
  { value: 'sw_g',                   label: 'Shot weight (g)' },
  { value: 'pw_g',                   label: 'Part weight (g)' },
  { value: 'toolchange_mount_min',   label: 'Mount time (min)' },
  { value: 'toolchange_unmount_min', label: 'Unmount time (min)' },
];

function SmartTab({ onResult }) {
  const fileRef = useRef();
  const [stage, setStage]           = useState('upload'); // upload | analysing | mapping | preview | importing
  const [rows, setRows]             = useState([]);
  const [rawRows, setRawRows]       = useState([]);
  const [columnMapping, setColumnMapping] = useState({});
  const [dataType, setDataType]     = useState('parts');
  const [summary, setSummary]       = useState('');
  const [error, setError]           = useState('');
  const [catalogue, setCatalogue]   = useState([]);
  const [editingMatchIdx, setEditingMatchIdx] = useState(null);
  const [matchSearch, setMatchSearch] = useState('');

  function updateRow(i, patch) {
    setRows(rs => rs.map((r, idx) => idx === i ? { ...r, ...patch } : r));
  }

  function toggleAction(i) {
    setRows(rs => rs.map((r, idx) => {
      if (idx !== i) return r;
      const next = r._action === 'update' ? 'create' : 'update';
      return { ...r, _action: next };
    }));
  }

  // Re-derive numeric field values from raw Excel rows using updated mapping.
  // Keeps AI-assigned name, match, action, is_product intact.
  function remapRows(newMapping, currentDataType) {
    const dt = currentDataType || dataType;
    const dataFields = dt === 'moulds' ? MOULDS_DATA_COLS : PARTS_DATA_COLS;
    const identityFields = ['name', 'ref', 'part_number', 'is_product', '_match', '_action', '_include'];
    setRows(prev => prev.map((aiRow, i) => {
      const raw = rawRows[i] || {};
      const updated = { ...aiRow };
      dataFields.forEach(f => delete updated[f]);
      for (const [rawCol, field] of Object.entries(newMapping)) {
        if (!field || field === 'skip' || identityFields.includes(field)) continue;
        const val = raw[rawCol];
        if (val != null && val !== '') {
          updated[field] = typeof val === 'number' ? val : (parseFloat(String(val)) || val);
        }
      }
      return updated;
    }));
  }

  async function analyse(raw, fileName) {
    setRawRows(raw);
    setStage('analysing');
    setError('');
    try {
      const { data } = await api.post('/api/v1/parts/import/analyse', { rows: raw, fileName }, { timeout: 90000 });
      if (!data.rows?.length) { setError('No records found in file.'); setStage('upload'); return; }
      setDataType(data.data_type || 'parts');
      setRows(data.rows.map(r => ({ ...r, _include: !r.is_product })));
      setSummary(data.summary || '');
      setColumnMapping(data.column_mapping || {});
      const catEndpoint = (data.data_type || 'parts') === 'moulds' ? '/api/v1/moulds' : '/api/v1/parts';
      api.get(catEndpoint).then(r => setCatalogue(r.data || [])).catch(() => {});
      setStage('mapping');
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setStage('upload');
    }
  }

  function handleFile(file) {
    if (!file) return;
    setError('');
    const ext = file.name.toLowerCase().split('.').pop();
    if (!['csv','xlsx','xls'].includes(ext)) {
      setError('Use CSV or Excel (.xlsx/.xls). For Word/PDF use the other tab.');
      return;
    }
    const reader = new FileReader();
    reader.onload = e => {
      try {
        const wb   = XLSX.read(e.target.result, { type: 'array' });
        const ws   = wb.Sheets[wb.SheetNames[0]];
        const data = XLSX.utils.sheet_to_json(ws, { defval: null });
        if (!data.length) { setError('Empty file'); return; }
        analyse(data, file.name);
      } catch (err) {
        setError('Could not parse file: ' + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
  }

  async function handleImport() {
    const toImport = rows.filter(r => r._include);
    if (!toImport.length) return;
    setStage('importing');
    try {
      const { data } = await api.post('/api/v1/parts/import', { rows: toImport, data_type: dataType });
      onResult(data, dataType);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
      setStage('preview');
    }
  }

  // ── Mapping ──
  if (stage === 'mapping') {
    const fieldOptions = dataType === 'moulds' ? MOULDS_FIELD_OPTIONS : PARTS_FIELD_OPTIONS;
    const sampleValues = {};
    Object.keys(columnMapping).forEach(col => {
      const found = rawRows.find(r => r[col] != null && r[col] !== '');
      sampleValues[col] = found != null ? String(found[col]) : '—';
    });
    return (
      <div>
        <button onClick={() => { setStage('upload'); setRows([]); setSummary(''); }}
          style={{ background: 'none', border: 'none', color: MUTED, fontSize: '12px', cursor: 'pointer', padding: '0 0 14px', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <i className="fa-solid fa-chevron-left" style={{ fontSize: '10px' }} /> Upload different file
        </button>
        {summary && (
          <div style={{ background: TEAL + '15', border: `1px solid ${TEAL}33`, borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '12px', color: TEAL }}>
            <i className="fa-solid fa-wand-magic-sparkles" style={{ marginRight: '8px' }} />{summary}
          </div>
        )}
        <div style={{ fontWeight: '700', fontSize: '14px', marginBottom: '4px' }}>Review column mapping</div>
        <div style={{ fontSize: '12px', color: MUTED, marginBottom: '16px' }}>
          AI mapped {Object.keys(columnMapping).length} columns. Adjust any that are wrong — values update instantly.
        </div>
        <div style={{ border: `1px solid ${BORDER}`, borderRadius: '8px', overflowX: 'auto', marginBottom: '20px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
            <thead>
              <tr style={{ background: '#161b22' }}>
                <th style={{ padding: '8px 14px', textAlign: 'left', color: MUTED, fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Your column</th>
                <th style={{ padding: '8px 14px', textAlign: 'left', color: MUTED, fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Maps to</th>
                <th style={{ padding: '8px 14px', textAlign: 'left', color: MUTED, fontSize: '11px', fontWeight: '600', textTransform: 'uppercase', letterSpacing: '0.05em' }}>Sample value</th>
              </tr>
            </thead>
            <tbody>
              {Object.entries(columnMapping).map(([rawCol, field]) => (
                <tr key={rawCol} style={{ borderTop: `1px solid ${BORDER}` }}>
                  <td style={{ padding: '8px 14px', fontWeight: '600' }}>{rawCol}</td>
                  <td style={{ padding: '6px 14px' }}>
                    <select
                      value={field || 'skip'}
                      onChange={e => {
                        const updated = { ...columnMapping, [rawCol]: e.target.value };
                        setColumnMapping(updated);
                        remapRows(updated);
                      }}
                      style={{ ...inp, width: 'auto', minWidth: '180px', padding: '5px 10px' }}
                    >
                      {fieldOptions.map(f => (
                        <option key={f.value} value={f.value}>{f.label}</option>
                      ))}
                    </select>
                  </td>
                  <td style={{ padding: '8px 14px', fontSize: '12px', color: MUTED, fontFamily: 'monospace' }}>
                    {sampleValues[rawCol]}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <button onClick={() => setStage('preview')}
          style={{ background: TEAL, border: 'none', color: '#fff', borderRadius: '8px', padding: '10px 24px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', display: 'flex', alignItems: 'center', gap: '8px' }}>
          Continue to preview
          <i className="fa-solid fa-arrow-right" />
        </button>
      </div>
    );
  }

  // ── Analysing ──
  if (stage === 'analysing') {
    return (
      <div style={{ textAlign: 'center', padding: '60px 0' }}>
        <i className="fa-solid fa-spinner fa-spin" style={{ fontSize: '32px', color: TEAL, marginBottom: '16px', display: 'block' }} />
        <div style={{ fontSize: '14px', fontWeight: '600' }}>Analysing with AI…</div>
        <div style={{ fontSize: '12px', color: MUTED, marginTop: '4px' }}>Detecting columns, matching existing parts</div>
      </div>
    );
  }

  // ── Preview ──
  if (stage === 'preview' || stage === 'importing') {
    const included  = rows.filter(r => r._include);
    const updates   = included.filter(r => r._action === 'update').length;
    const creates   = included.filter(r => r._action === 'create').length;
    const allDataCols = dataType === 'moulds' ? MOULDS_DATA_COLS : PARTS_DATA_COLS;
    const activeCols  = allDataCols.filter(f => rows.some(r => r[f] != null));

    return (
      <div>
        <button onClick={() => { setStage('upload'); setRows([]); setSummary(''); }}
          style={{ background: 'none', border: 'none', color: MUTED, fontSize: '12px', cursor: 'pointer', padding: '0 0 14px', display: 'flex', alignItems: 'center', gap: '4px' }}>
          <i className="fa-solid fa-chevron-left" style={{ fontSize: '10px' }} /> Upload different file
        </button>

        {summary && (
          <div style={{ background: TEAL + '15', border: `1px solid ${TEAL}33`, borderRadius: '8px', padding: '10px 14px', marginBottom: '12px', fontSize: '12px', color: TEAL }}>
            <i className="fa-solid fa-wand-magic-sparkles" style={{ marginRight: '8px' }} />{summary}
          </div>
        )}

        <div style={{ fontSize: '12px', color: MUTED, marginBottom: '10px' }}>
          <span style={{ color: TEAL, fontWeight: '700' }}>{updates}</span> to update ·{' '}
          <span style={{ color: '#93c5fd', fontWeight: '700' }}>{creates}</span> to create ·{' '}
          {rows.filter(r => !r._include).length} skipped
          <span style={{ marginLeft: '12px', fontSize: '11px' }}>— click UPDATE/CREATE badge to toggle, uncheck to skip</span>
        </div>

        <div style={{ overflowX: 'auto', maxHeight: '420px', overflowY: 'auto', border: `1px solid ${BORDER}`, borderRadius: '8px' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead style={{ position: 'sticky', top: 0, background: '#161b22', zIndex: 1 }}>
              <tr>
                <th style={{ padding: '8px 10px', width: '32px' }} />
                <th style={{ padding: '8px 10px', textAlign: 'left', color: MUTED }}>Name</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', color: MUTED, whiteSpace: 'nowrap' }}>Action</th>
                <th style={{ padding: '8px 10px', textAlign: 'left', color: MUTED, whiteSpace: 'nowrap' }}>Matched to</th>
                {activeCols.map(c => (
                  <th key={c} style={{ padding: '8px 10px', textAlign: 'right', color: MUTED, whiteSpace: 'nowrap' }}>{COL_LABELS[c]}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i} style={{
                  borderTop: `1px solid ${BORDER}22`,
                  opacity: row._include ? 1 : 0.35,
                  background: row.is_product ? '#161b22' : '',
                }}>
                  <td style={{ padding: '6px 10px' }}>
                    <input type="checkbox" checked={!!row._include}
                      onChange={() => updateRow(i, { _include: !row._include })}
                      style={{ accentColor: TEAL, cursor: 'pointer' }} />
                  </td>
                  <td style={{ padding: '5px 8px' }}>
                    <div style={{ fontWeight: '600', color: row.is_product ? TEAL : TEXT }}>{row.name}</div>
                    {row.is_product && <div style={{ fontSize: '10px', color: MUTED }}>Assembly — skipped by default</div>}
                    {dataType === 'moulds'
                      ? row.ref        && <div style={{ fontSize: '10px', color: TEAL, fontFamily: 'monospace' }}>{row.ref}</div>
                      : row.part_number && <div style={{ fontSize: '10px', color: TEAL, fontFamily: 'monospace' }}>{row.part_number}</div>
                    }
                  </td>
                  <td style={{ padding: '5px 8px' }}>
                    {!row.is_product && row._include && (
                      <ActionBadge action={row._action} onClick={() => toggleAction(i)} />
                    )}
                  </td>
                  <td style={{ padding: '5px 8px', fontSize: '11px', color: MUTED, maxWidth: '200px' }}>
                    {editingMatchIdx === i ? (
                      <div style={{ position: 'relative' }}>
                        <input
                          autoFocus
                          value={matchSearch}
                          onChange={e => setMatchSearch(e.target.value)}
                          placeholder="Search…"
                          style={{ ...inp, padding: '4px 8px', fontSize: '12px', width: '170px' }}
                          onBlur={() => setTimeout(() => { setEditingMatchIdx(null); setMatchSearch(''); }, 150)}
                        />
                        <div style={{ position: 'absolute', top: '100%', left: 0, width: '240px', background: CARD, border: `1px solid ${BORDER}`, borderRadius: '6px', zIndex: 20, maxHeight: '220px', overflowY: 'auto', marginTop: '2px', boxShadow: '0 8px 24px rgba(0,0,0,0.5)' }}>
                          <div
                            onMouseDown={() => { updateRow(i, { _match: null, _action: 'create' }); setEditingMatchIdx(null); setMatchSearch(''); }}
                            style={{ padding: '7px 10px', cursor: 'pointer', borderBottom: `1px solid ${BORDER}`, color: '#f59e0b', fontSize: '12px', fontWeight: '600', display: 'flex', alignItems: 'center', gap: '6px' }}
                            onMouseEnter={e => e.currentTarget.style.background = '#242b35'}
                            onMouseLeave={e => e.currentTarget.style.background = ''}
                          >
                            <i className="fa-solid fa-plus" /> Create new instead
                          </div>
                          {catalogue
                            .filter(c => {
                              const q = matchSearch.toLowerCase();
                              return !q || (c.name || '').toLowerCase().includes(q) || (c.part_number || c.ref || '').toLowerCase().includes(q);
                            })
                            .slice(0, 12)
                            .map(c => (
                              <div
                                key={c.id}
                                onMouseDown={() => { updateRow(i, { _match: { ...c, score: 1.0 }, _action: 'update' }); setEditingMatchIdx(null); setMatchSearch(''); }}
                                style={{ padding: '6px 10px', cursor: 'pointer', borderBottom: `1px solid ${BORDER}22`, fontSize: '12px' }}
                                onMouseEnter={e => e.currentTarget.style.background = '#242b35'}
                                onMouseLeave={e => e.currentTarget.style.background = ''}
                              >
                                <div style={{ fontWeight: '600', color: TEXT }}>{c.name}</div>
                                {(c.part_number || c.ref) && <div style={{ fontSize: '10px', color: TEAL, fontFamily: 'monospace' }}>{c.part_number || c.ref}</div>}
                              </div>
                            ))
                          }
                        </div>
                      </div>
                    ) : (
                      <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                        <span>
                          {row._action === 'update' && row._match ? (
                            <>
                              <span style={{ color: TEXT }}>{row._match.name}</span>
                              {row._match.score < 0.99 && (
                                <span style={{ fontSize: '9px', color: '#f59e0b', marginLeft: '4px', fontWeight: '700' }}>
                                  {Math.round(row._match.score * 100)}%
                                </span>
                              )}
                            </>
                          ) : (
                            <span style={{ color: MUTED + '60' }}>—</span>
                          )}
                        </span>
                        {!row.is_product && row._include && (
                          <button
                            onClick={() => { setEditingMatchIdx(i); setMatchSearch(''); }}
                            title="Change match"
                            style={{ background: 'none', border: 'none', color: MUTED, cursor: 'pointer', padding: '2px 4px', fontSize: '11px', flexShrink: 0 }}
                          >
                            <i className="fa-solid fa-pen-to-square" />
                          </button>
                        )}
                      </div>
                    )}
                  </td>
                  {activeCols.map(c => (
                    <td key={c} style={{ padding: '5px 8px', textAlign: 'right', color: row[c] != null ? TEXT : MUTED + '60' }}>
                      {row[c] != null ? row[c] : '—'}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {error && (
          <div style={{ color: RED, fontSize: '12px', marginTop: '12px', background: RED + '15', border: `1px solid ${RED}33`, borderRadius: '8px', padding: '10px 14px' }}>
            {error}
          </div>
        )}

        <button
          disabled={!included.length || stage === 'importing'}
          onClick={handleImport}
          style={{
            marginTop: '16px', background: TEAL, border: 'none', color: '#fff',
            borderRadius: '8px', padding: '10px 24px', fontSize: '13px', fontWeight: '600',
            cursor: included.length ? 'pointer' : 'not-allowed',
            opacity: (!included.length || stage === 'importing') ? 0.6 : 1,
            display: 'flex', alignItems: 'center', gap: '8px',
          }}>
          {stage === 'importing' && <i className="fa-solid fa-spinner fa-spin" />}
          {stage === 'importing' ? 'Importing…' : `Import ${updates + creates} ${dataType}`}
        </button>
      </div>
    );
  }

  // ── Upload ──
  return (
    <div>
      <div style={{ fontSize: '12px', color: MUTED, marginBottom: '16px' }}>
        AI will automatically detect columns in any language, identify what was uploaded, and match against your existing catalogue.
      </div>
      <div
        onDragOver={e => { e.preventDefault(); e.currentTarget.style.borderColor = TEAL; }}
        onDragLeave={e => { e.currentTarget.style.borderColor = BORDER; }}
        onDrop={e => {
          e.preventDefault();
          e.currentTarget.style.borderColor = BORDER;
          handleFile(e.dataTransfer.files[0]);
        }}
        onClick={() => fileRef.current.click()}
        style={{
          border: `2px dashed ${BORDER}`, borderRadius: '12px', padding: '52px 24px',
          textAlign: 'center', cursor: 'pointer', transition: 'border-color 0.15s',
        }}
      >
        <i className="fa-solid fa-file-arrow-up" style={{ fontSize: '36px', color: MUTED, marginBottom: '14px', display: 'block' }} />
        <div style={{ fontWeight: '600', fontSize: '14px', marginBottom: '6px' }}>Drop a file here, or click to browse</div>
        <div style={{ fontSize: '12px', color: MUTED }}>CSV · Excel (.xlsx, .xls)</div>
      </div>
      <input ref={fileRef} type="file" accept=".xlsx,.xls,.csv" style={{ display: 'none' }}
        onChange={e => { handleFile(e.target.files[0]); e.target.value = ''; }} />
      {error && <div style={{ color: RED, fontSize: '12px', marginTop: '12px' }}>{error}</div>}
    </div>
  );
}

// ── Word / PDF tab (AI extraction, basic) ─────────────────────────────────────

function PreviewTable({ rows, onChange }) {
  function toggle(i) { onChange(rows.map((r, idx) => idx === i ? { ...r, _skip: !r._skip } : r)); }
  function edit(i, f, v) { onChange(rows.map((r, idx) => idx === i ? { ...r, [f]: v } : r)); }
  const active = rows.filter(r => !r._skip);
  return (
    <div>
      <div style={{ fontSize: '12px', color: MUTED, marginBottom: '8px' }}>
        {active.length} of {rows.length} parts selected — uncheck rows to skip
      </div>
      <div style={{ overflowX: 'auto', maxHeight: '360px', overflowY: 'auto', border: `1px solid ${BORDER}`, borderRadius: '8px' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
          <thead style={{ position: 'sticky', top: 0, background: '#161b22' }}>
            <tr>
              <th style={{ padding: '8px 10px', textAlign: 'left', color: MUTED, width: '32px' }} />
              <th style={{ padding: '8px 10px', textAlign: 'left', color: MUTED }}>Part Number</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', color: MUTED }}>Name</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', color: MUTED }}>Per Box</th>
              <th style={{ padding: '8px 10px', textAlign: 'left', color: MUTED }}>Per Pallet</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((row, i) => (
              <tr key={i} style={{ borderTop: `1px solid ${BORDER}22`, opacity: row._skip ? 0.35 : 1 }}>
                <td style={{ padding: '6px 10px' }}>
                  <input type="checkbox" checked={!row._skip} onChange={() => toggle(i)}
                    style={{ accentColor: TEAL, width: '14px', height: '14px', cursor: 'pointer' }} />
                </td>
                <td style={{ padding: '4px 6px' }}>
                  <input value={row.part_number || ''} onChange={e => edit(i, 'part_number', e.target.value)}
                    style={{ ...inp, padding: '4px 8px', width: '120px' }} placeholder="—" />
                </td>
                <td style={{ padding: '4px 6px' }}>
                  <input value={row.name || ''} onChange={e => edit(i, 'name', e.target.value)}
                    style={{ ...inp, padding: '4px 8px' }} placeholder="Part name" />
                </td>
                <td style={{ padding: '4px 6px' }}>
                  <input type="number" min="1" value={row.qty_per_box || ''} onChange={e => edit(i, 'qty_per_box', e.target.value)}
                    style={{ ...inp, padding: '4px 8px', width: '80px' }} placeholder="—" />
                </td>
                <td style={{ padding: '4px 6px' }}>
                  <input type="number" min="1" value={row.qty_per_pallet || ''} onChange={e => edit(i, 'qty_per_pallet', e.target.value)}
                    style={{ ...inp, padding: '4px 8px', width: '80px' }} placeholder="—" />
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function AiTab({ onReady }) {
  const fileRef  = useRef();
  const [file, setFile]       = useState(null);
  const [parsing, setParsing] = useState(false);
  const [previewRows, setPreviewRows] = useState(null);
  const [error, setError]     = useState('');

  async function handleParse() {
    if (!file) return;
    setParsing(true); setError('');
    try {
      const form = new FormData();
      form.append('file', file);
      const { data } = await api.post('/api/v1/parts/import/parse', form, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      if (!data.parts?.length) { setError('No parts found — try Excel instead.'); return; }
      setPreviewRows(data.parts);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setParsing(false);
    }
  }

  if (previewRows) return (
    <div>
      <button onClick={() => { setPreviewRows(null); setFile(null); fileRef.current.value = ''; }}
        style={{ background: 'none', border: 'none', color: MUTED, fontSize: '12px', cursor: 'pointer', padding: '0 0 12px', display: 'flex', alignItems: 'center', gap: '4px' }}>
        <i className="fa-solid fa-chevron-left" style={{ fontSize: '10px' }} /> Back
      </button>
      <div style={{ background: TEAL + '15', border: `1px solid ${TEAL}33`, borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '12px', color: TEAL }}>
        <i className="fa-solid fa-wand-magic-sparkles" style={{ marginRight: '8px' }} />
        AI extracted {previewRows.length} parts. Review before importing.
      </div>
      <PreviewTable rows={previewRows} onChange={setPreviewRows} />
      <button onClick={() => onReady(previewRows.filter(r => !r._skip && r.name).map(r => ({ ...r, _action: 'create' })))}
        style={{ marginTop: '16px', background: TEAL, border: 'none', color: '#fff', borderRadius: '8px', padding: '10px 24px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
        Import {previewRows.filter(r => !r._skip && r.name).length} parts
      </button>
    </div>
  );

  return (
    <div>
      <div style={{ background: '#f59e0b15', border: `1px solid #f59e0b33`, borderRadius: '8px', padding: '10px 14px', marginBottom: '16px', fontSize: '12px', color: '#fbbf24' }}>
        <i className="fa-solid fa-triangle-exclamation" style={{ marginRight: '8px' }} />
        Extracts parts list only (name, part number, quantities). For cycle times and weights use the Smart Import tab with a spreadsheet.
      </div>
      <div style={{ fontSize: '11px', fontWeight: '600', color: MUTED, textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: '6px' }}>Upload file</div>
      <div style={{ fontSize: '11px', color: MUTED, marginBottom: '10px' }}>Supported: .docx, .pdf</div>
      <input ref={fileRef} type="file" accept=".docx,.pdf" style={{ display: 'none' }}
        onChange={e => setFile(e.target.files[0] || null)} />
      <div style={{ display: 'flex', gap: '10px', alignItems: 'center', marginBottom: '16px' }}>
        <button onClick={() => fileRef.current.click()}
          style={{ background: '#161b22', border: `1px solid ${BORDER}`, color: TEXT, borderRadius: '8px', padding: '8px 16px', fontSize: '13px', cursor: 'pointer' }}>
          <i className="fa-solid fa-upload" style={{ marginRight: '8px', color: TEAL }} />
          {file ? file.name : 'Choose file…'}
        </button>
        {file && <button onClick={() => { setFile(null); fileRef.current.value = ''; }}
          style={{ background: 'none', border: 'none', color: MUTED, cursor: 'pointer', fontSize: '12px' }}>Clear</button>}
      </div>
      {error && <div style={{ color: RED, fontSize: '12px', marginBottom: '10px' }}>{error}</div>}
      {file && (
        <button onClick={handleParse} disabled={parsing}
          style={{ background: TEAL, border: 'none', color: '#fff', borderRadius: '8px', padding: '10px 24px', fontSize: '13px', fontWeight: '600', cursor: parsing ? 'not-allowed' : 'pointer', opacity: parsing ? 0.7 : 1, display: 'flex', alignItems: 'center', gap: '8px' }}>
          <i className={`fa-solid ${parsing ? 'fa-spinner fa-spin' : 'fa-wand-magic-sparkles'}`} />
          {parsing ? 'Extracting…' : 'Parse with AI'}
        </button>
      )}
    </div>
  );
}

// ── Result screen ─────────────────────────────────────────────────────────────

function ResultScreen({ result, dataType, onImportMore, onGoBack }) {
  const noun = dataType === 'moulds' ? 'moulds' : 'parts';
  return (
    <div style={{ textAlign: 'center', padding: '40px 0' }}>
      <i className="fa-solid fa-circle-check" style={{ fontSize: '40px', color: TEAL, marginBottom: '16px', display: 'block' }} />
      <div style={{ fontSize: '18px', fontWeight: '700', marginBottom: '8px' }}>Import complete</div>
      <div style={{ color: MUTED, fontSize: '13px', marginBottom: '4px' }}>
        <span style={{ color: TEAL, fontWeight: '700' }}>{result.updated?.length || 0}</span> {noun} updated ·{' '}
        <span style={{ color: '#93c5fd', fontWeight: '700' }}>{result.created?.length || 0}</span> {noun} created
      </div>
      {(result.skippedCount > 0) && (
        <div style={{ color: MUTED, fontSize: '12px', marginBottom: '24px' }}>{result.skippedCount} skipped</div>
      )}
      {result.notes?.length > 0 && (
        // Box and pallet numbers go into a part's packaging layers; the ones
        // that couldn't, and why (30 Sep 2026).
        <div style={{ background: '#f59e0b15', border: '1px solid #f59e0b33', borderRadius: '8px', padding: '12px 16px', margin: '20px auto 0', maxWidth: '640px', textAlign: 'left', fontSize: '12px', color: TEXT }}>
          <div style={{ color: '#fbbf24', fontWeight: '700', marginBottom: '8px' }}>
            <i className="fa-solid fa-box-open" style={{ marginRight: '6px' }} />
            Some box and pallet numbers were not used
          </div>
          {result.notes.map((n, i) => (
            <div key={i} style={{ padding: '4px 0', lineHeight: 1.5 }}>
              <span style={{ fontWeight: '600' }}>{n.name}:</span> <span style={{ color: '#cbd5e1' }}>{n.says}</span>
            </div>
          ))}
        </div>
      )}
      <div style={{ display: 'flex', gap: '10px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '24px' }}>
        <button onClick={onImportMore}
          style={{ background: CARD, border: `1px solid ${BORDER}`, color: TEXT, borderRadius: '8px', padding: '9px 20px', fontSize: '13px', cursor: 'pointer' }}>
          Import more
        </button>
        <button onClick={onGoBack}
          style={{ background: TEAL, border: 'none', color: '#fff', borderRadius: '8px', padding: '9px 20px', fontSize: '13px', fontWeight: '600', cursor: 'pointer' }}>
          Back to Parts
        </button>
      </div>
    </div>
  );
}

// ── Main page ─────────────────────────────────────────────────────────────────

export default function AdminPartsImport() {
  const navigate = useNavigate();
  const goBack   = useBack('/admin');
  const [tab, setTab]           = useState('smart');
  const [importing, setImporting] = useState(false);
  const [result, setResult]     = useState(null);
  const [lastDataType, setLastDataType] = useState('parts');
  const [error, setError]       = useState('');

  async function handleImport(rows) {
    if (!rows.length) return;
    setImporting(true); setError('');
    try {
      const { data } = await api.post('/api/v1/parts/import', { rows });
      setResult(data);
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setImporting(false);
    }
  }

  const TABS = [
    { key: 'smart', label: 'Smart Import',  icon: 'fa-solid fa-wand-magic-sparkles' },
    { key: 'ai',    label: 'Word / PDF',     icon: 'fa-solid fa-file-word' },
  ];

  return (
    <div style={{ padding: '20px', fontFamily: 'system-ui, sans-serif', color: TEXT, background: BG, minHeight: '100vh' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '4px' }}>
        <button onClick={goBack}
          style={{ background: 'none', border: 'none', color: MUTED, fontSize: '12px', cursor: 'pointer', padding: 0, display: 'flex', alignItems: 'center', gap: '4px' }}>
          <i className="fa-solid fa-chevron-left" style={{ fontSize: '10px' }} /> Back
        </button>
        <span style={{ color: BORDER }}>·</span>
        <span style={{ fontSize: '12px', color: TEXT }}>Mass Upload</span>
      </div>

      <h1 style={{ fontSize: '22px', fontWeight: '700', marginBottom: '4px', marginTop: '8px' }}>Mass Upload</h1>
      <p style={{ color: MUTED, marginBottom: '24px', fontSize: '13px' }}>
        Upload any spreadsheet or document — AI detects what it contains and matches against your existing catalogue.
      </p>

      {result ? (
        <div style={{ maxWidth: '520px', background: CARD, border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '24px' }}>
          <ResultScreen result={result} dataType={lastDataType} onImportMore={() => setResult(null)} onGoBack={() => navigate('/admin')} />
        </div>
      ) : (
        <div style={{ maxWidth: '820px', background: CARD, border: `1px solid ${BORDER}`, borderRadius: '12px', padding: '24px' }}>
          <div style={{ display: 'flex', borderBottom: `1px solid ${BORDER}`, marginBottom: '24px' }}>
            {TABS.map(t => (
              <button key={t.key} onClick={() => setTab(t.key)}
                style={{
                  background: 'none', border: 'none',
                  borderBottom: tab === t.key ? `2px solid ${TEAL}` : '2px solid transparent',
                  color: tab === t.key ? TEXT : MUTED,
                  padding: '8px 18px', fontSize: '13px', fontWeight: '600', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: '6px',
                }}>
                <i className={t.icon} style={{ fontSize: '12px' }} />{t.label}
              </button>
            ))}
          </div>

          {importing ? (
            <div style={{ textAlign: 'center', padding: '40px 0', color: MUTED, fontSize: '13px' }}>
              <i className="fa-solid fa-spinner fa-spin" style={{ marginRight: '8px' }} />Importing…
            </div>
          ) : (
            <>
              {tab === 'smart' && <SmartTab onResult={(r, dt) => { setResult(r); setLastDataType(dt || 'parts'); }} />}
              {tab === 'ai'    && <AiTab    onReady={handleImport} />}
            </>
          )}

          {error && (
            <div style={{ color: RED, fontSize: '12px', marginTop: '16px', background: RED + '15', border: `1px solid ${RED}33`, borderRadius: '8px', padding: '10px 14px' }}>
              {error}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
